/**
 * Who to offer, right inside the assign control (owner, 2026-10-07: "when assigning task, suggesting to assign to the same
 * thing in the list as the last one would speed things up ... Show More can show all employees available. 6AM or opening
 * tasks should default to suggest person who opens and late night who ever closes"). Pure, so the API and both demos agree.
 *
 * | S1 Moment  | A task due at opening (by the place's open time + 1 hour; with no hours, before 10 am) is an OPENING task. One due late (from 90 minutes before closing, or after 8 pm) is a CLOSING task. Anything else is a day task |
 * | S2 Opener  | The person who usually opens: on the same weekday, the earliest check-in of each of the last 8 weeks' days (anyone within 15 minutes of the first counts). Two or more of those days: "Opens Mondays". Fewer: the person scheduled today (their work days), the person in charge first: "Scheduled today" |
 * | S3 Closer  | The person who usually closes: on the same weekday, whoever recorded the most things in the last 90 minutes before closing, over the last 8 weeks. Two or more days: "Closes Fridays". Fewer: as S2 |
 * | S4 Last    | Whoever had this same task last time (the check's base key, "close-cold" for "close-cold@22:00"): "Had it last time"; one who stepped in: "Did it last time" |
 * | S5 Mine    | Whoever the person asking assigned anything to most recently: "Your last pick" |
 * | S6 Order   | Up to 3 chips, in the order S2/S3 (by the moment), S4, S5; one person never twice (the reasons join); anyone turned off or past their last day never |
 * | S7 More    | Everyone available: scheduled or on shift today first, then the rest, each A to Z. Turned-off people are not listed |
 * | S8 Privacy | Only check-in presence (who, which day, what time), records done, schedules and past assignments. Never a health answer or "ok to work" (FRAMEWORK T4): the inputs below have no such field |
 *
 * ASSUMED (judgment defaults, not law): the 8-week window, the 2-day minimum, the 15-minute opening window, the 90-minute and 1-hour margins.
 */
import { OPENER_WINDOW_MIN, baseKey, type ShiftPerson, type TaskAssignment } from "./delegate.ts";
import { DAY_NAMES, addLocalDays, dayKeyOf, hoursOn, localDateMinutes, spanMinutes, type HoursSource } from "./hours.ts";
import type { Id, IsoDate, IsoDateTime } from "./model.ts";

export const SUGGEST_MAX = 3;
export const SUGGEST_WEEKS = 8;
export const SUGGEST_MIN_DAYS = 2;
export const CLOSING_WINDOW_MIN = 90;
export const OPENING_MARGIN_MIN = 60;
const NO_HOURS_OPENING_BEFORE = 10 * 60;
const LATE_NIGHT_FROM = 20 * 60;

/** Presence only: who was there, which day, when. Never a health answer. */
export interface PresenceRecord { employeeId: Id; date: IsoDate; at: IsoDateTime }
/** Something a person recorded as done (a log entry, a quick tap), when. */
export interface DoneRecord { employeeId: Id; at: IsoDateTime }

export interface SuggestHistory {
  tz: string;
  hours: HoursSource;
  /** Check-ins at this place over the last weeks (employeeId, date, at: nothing else is read). */
  checkins: readonly PresenceRecord[];
  /** What people recorded as done over the same weeks. */
  done: readonly DoneRecord[];
}

/** S8: the only way check-ins enter: who, which day, when. A health answer or okToWork is never copied out. */
export const presenceOf = (rows: readonly { employeeId: Id; date: IsoDate; at: IsoDateTime }[]): PresenceRecord[] =>
  rows.map((r) => ({ employeeId: r.employeeId, date: r.date, at: r.at }));

/** Records done, by employee: a log entry's author (a sign-in id) mapped to their employee record. Records marked not done after all are left out. */
export function doneRecordsOf(entries: readonly { createdBy: Id; createdAt: IsoDateTime; correction?: unknown }[], employees: readonly { id: Id; userId?: Id }[]): DoneRecord[] {
  const byUser = new Map(employees.filter((e) => e.userId).map((e) => [e.userId!, e.id] as const));
  const out: DoneRecord[] = [];
  for (const e of entries) { const id = byUser.get(e.createdBy); if (id && !e.correction) out.push({ employeeId: id, at: e.createdAt }); }
  return out;
}

/** The first day the history needs: SUGGEST_WEEKS weeks back. */
export const suggestSince = (date: IsoDate): IsoDate => addLocalDays(date, -7 * SUGGEST_WEEKS);

export type SuggestSource = "opener" | "closer" | "scheduled" | "last_for_task" | "last_by_me";
export interface Suggestion { employeeId: Id; name: string; why: string; sources: SuggestSource[] }
export type TaskMoment = "opening" | "closing" | "day";

/** S1. `date` is the shift day; `dueAt` an instant. */
export function taskMoment(dueAt: IsoDateTime, date: IsoDate, h: Pick<SuggestHistory, "tz" | "hours">): TaskMoment {
  const l = localDateMinutes(dueAt, h.tz);
  const min = l.min + (l.date > date ? 1440 : 0);
  const span = hoursOn(h.hours, date);
  if (span === "closed") return min < NO_HOURS_OPENING_BEFORE ? "opening" : min >= LATE_NIGHT_FROM ? "closing" : "day";
  const { openMin, closeMin } = spanMinutes(span);
  if (min <= openMin + OPENING_MARGIN_MIN) return "opening";
  if (min >= closeMin - CLOSING_WINDOW_MIN || min >= LATE_NIGHT_FROM) return "closing";
  return "day";
}

const pastSameWeekday = (date: IsoDate): IsoDate[] => {
  const out: IsoDate[] = [];
  for (let w = 1; w <= SUGGEST_WEEKS; w++) out.push(addLocalDays(date, -7 * w));
  return out;
};

interface Tally { count: number; last: IsoDate }
const bump = (m: Map<Id, Tally>, id: Id, date: IsoDate) => { const t = m.get(id); if (t) { t.count++; if (date > t.last) t.last = date; } else m.set(id, { count: 1, last: date }); };

/** S2: how often each person opened on this weekday over the last weeks. */
export function openerTally(date: IsoDate, h: Pick<SuggestHistory, "checkins">): Map<Id, Tally> {
  const days = new Set(pastSameWeekday(date));
  const byDay = new Map<IsoDate, PresenceRecord[]>();
  for (const c of h.checkins) if (days.has(c.date)) byDay.set(c.date, [...(byDay.get(c.date) ?? []), c]);
  const out = new Map<Id, Tally>();
  for (const [d, list] of byDay) {
    const first = Math.min(...list.map((c) => Date.parse(c.at)));
    const seen = new Set<Id>();
    for (const c of list) if (Date.parse(c.at) - first <= OPENER_WINDOW_MIN * 60_000 && !seen.has(c.employeeId)) { seen.add(c.employeeId); bump(out, c.employeeId, d); }
  }
  return out;
}

/** S3: how often each person did the most in the closing window on this weekday over the last weeks. */
export function closerTally(date: IsoDate, h: Pick<SuggestHistory, "done" | "tz" | "hours">): Map<Id, Tally> {
  const days = new Set(pastSameWeekday(date));
  const perDay = new Map<IsoDate, Map<Id, number>>();
  for (const r of h.done) {
    const l = localDateMinutes(r.at, h.tz);
    let d = l.date, min = l.min;
    const prev = addLocalDays(d, -1);
    const ps = hoursOn(h.hours, prev);
    // An overnight close: the small hours after midnight belong to the day before.
    if (ps !== "closed" && spanMinutes(ps).closeMin > 1440 && min <= spanMinutes(ps).closeMin - 1440) { d = prev; min += 1440; }
    if (!days.has(d)) continue;
    const span = hoursOn(h.hours, d);
    if (span === "closed" || min < spanMinutes(span).closeMin - CLOSING_WINDOW_MIN) continue;
    const m = perDay.get(d) ?? new Map<Id, number>();
    m.set(r.employeeId, (m.get(r.employeeId) ?? 0) + 1);
    perDay.set(d, m);
  }
  const out = new Map<Id, Tally>();
  for (const [d, m] of perDay) {
    const top = Math.max(...m.values());
    for (const [id, n] of m) if (n === top) bump(out, id, d);
  }
  return out;
}

const available = (p: ShiftPerson, date: IsoDate) => !(p.endsOn && p.endsOn < date);
const weekdayNum = (date: IsoDate) => new Date(`${date}T12:00:00Z`).getUTCDay();
export const isScheduled = (p: Pick<ShiftPerson, "workDays">, date: IsoDate) => !!p.workDays?.includes(weekdayNum(date));
const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, "en", { sensitivity: "base" });
const manager = (p: ShiftPerson) => (p.role && p.role !== "staff" ? 1 : 0);

/** The most regular person in a tally with at least SUGGEST_MIN_DAYS days, among people still available. */
function regular(t: Map<Id, Tally>, people: readonly ShiftPerson[], date: IsoDate): ShiftPerson | undefined {
  return people.filter((p) => available(p, date) && (t.get(p.id)?.count ?? 0) >= SUGGEST_MIN_DAYS)
    .sort((a, b) => t.get(b.id)!.count - t.get(a.id)!.count || t.get(b.id)!.last.localeCompare(t.get(a.id)!.last) || byName(a, b))[0];
}

/** S2/S3 fallback: the person scheduled today, the person in charge, then a manager, first. */
function scheduledToday(people: readonly ShiftPerson[], date: IsoDate): ShiftPerson | undefined {
  return people.filter((p) => available(p, date) && isScheduled(p, date))
    .sort((a, b) => Number(b.isPersonInCharge) - Number(a.isPersonInCharge) || manager(b) - manager(a) || byName(a, b))[0];
}

export interface SuggestInput {
  itemKey: string;
  dueAt: IsoDateTime;
  date: IsoDate;
  people: readonly ShiftPerson[];
  events: readonly TaskAssignment[];
  history: SuggestHistory;
  /** The person asking (S5). */
  meId?: Id;
}

/** S6: up to three people to offer for this task. */
export function suggestAssignees(i: SuggestInput): Suggestion[] {
  const day = DAY_NAMES[dayKeyOf(i.date)];
  const moment = taskMoment(i.dueAt, i.date, i.history);
  const out: Suggestion[] = [];
  const add = (p: ShiftPerson | undefined, why: string, source: SuggestSource) => {
    if (!p || !available(p, i.date)) return;
    const have = out.find((x) => x.employeeId === p.id);
    if (have) { if (!have.sources.includes(source)) { have.sources.push(source); have.why = `${have.why} · ${why}`; } return; }
    if (out.length < SUGGEST_MAX) out.push({ employeeId: p.id, name: p.name, why, sources: [source] });
  };
  if (moment === "opening") {
    const p = regular(openerTally(i.date, i.history), i.people, i.date);
    if (p) add(p, `Opens ${day}s`, "opener"); else { const s = scheduledToday(i.people, i.date); if (s) add(s, "Scheduled today", "scheduled"); }
  } else if (moment === "closing") {
    const p = regular(closerTally(i.date, i.history), i.people, i.date);
    if (p) add(p, `Closes ${day}s`, "closer"); else { const s = scheduledToday(i.people, i.date); if (s) add(s, "Scheduled today", "scheduled"); }
  }
  const base = baseKey(i.itemKey);
  const person = (id: Id | undefined) => id ? i.people.find((p) => p.id === id) : undefined;
  const lastFor = [...i.events].filter((e) => baseKey(e.itemKey) === base && e.employeeId && ((e.action === "assign" && e.targetKind === "person") || e.action === "step_in"))
    .sort((a, b) => b.at.localeCompare(a.at))[0];
  if (lastFor) add(person(lastFor.employeeId), lastFor.action === "step_in" ? "Did it last time" : "Had it last time", "last_for_task");
  if (i.meId) {
    const mine = [...i.events].filter((e) => e.action === "assign" && e.targetKind === "person" && e.employeeId && e.byEmployeeId === i.meId).sort((a, b) => b.at.localeCompare(a.at))[0];
    if (mine) add(person(mine.employeeId), "Your last pick", "last_by_me");
  }
  return out;
}

/** S7: everyone available: scheduled or on shift today first, then the rest, each A to Z. */
export function availablePeople<T extends ShiftPerson>(people: readonly T[], date: IsoDate): (T & { scheduled: boolean })[] {
  const list = people.filter((p) => available(p, date)).map((p) => ({ ...p, scheduled: isScheduled(p, date) }));
  const first = (p: { scheduled: boolean; onShift: boolean }) => p.scheduled || p.onShift;
  return list.sort((a, b) => Number(first(b)) - Number(first(a)) || byName(a, b));
}

export const SUGGEST_WORDS = {
  suggested: "Suggested",
  showMore: "Show more people",
  showFewer: "Show fewer",
  gave: (name: string) => `Given to ${name}.`,
  undo: "Undo",
  undone: "Back to anyone on shift.",
} as const;
